import React from "react";
import { ActionIcon, createStyles, Divider, Skeleton } from "@mantine/core";
import { useClipboard } from "@mantine/hooks";
import { Button, tokens } from "@blurb/codex-react";
import { GraphQL } from "@rpi/openapi-api";
import { IconV3 } from "@rpi/openapi-core";
import { Link, useParams } from "react-router-dom";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { getRoutePath } from "../../../hooks/useNavigateRoute";
import { Body } from "../../../components/Typography";
import { DetailField } from "./order-details/DetailField";
import { PaymentCard } from "./order-details/PaymentCard";
import { RefundCard } from "./order-details/RefundCard";
import { flexColumn, formatOrderDate, isReprintOrder, linkStyle, type Order } from "./order-details/shared";

const useStyles = createStyles(() => ({
  container: {
    ...flexColumn,
    gap: tokens.spacing[6],
    padding: tokens.spacing[4],
  },
  row: {
    display: "flex",
    gap: tokens.spacing[6],
    paddingLeft: tokens.spacing[4],
    paddingRight: tokens.spacing[4],
  },
  cardsRow: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: tokens.spacing[6],
  },
  link: linkStyle,
  reprintRow: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacing[1],
  },
  skeletonField: {
    flex: "1 0 0",
    minWidth: 0,
  },
  skeletonCard: {
    flex: "1 0 0",
    minWidth: 280,
    maxWidth: "50%",
  },
  errorContainer: {
    ...flexColumn,
    alignItems: "center",
    justifyContent: "center",
    padding: tokens.spacing[10],
    gap: tokens.spacing[4],
  },
}));

const SkeletonField: React.FC = () => {
  const { classes } = useStyles();
  return (
    <div className={classes.skeletonField}>
      <Skeleton height={14} width={100} mb={tokens.spacing[1]} />
      <Skeleton height={16} width={160} />
    </div>
  );
};

const SkeletonCard: React.FC = () => {
  const { classes } = useStyles();
  return (
    <div className={classes.skeletonCard}>
      <Skeleton height={180} radius="sm" />
    </div>
  );
};

const OrderDetailsSkeleton: React.FC = () => {
  const { classes } = useStyles();
  return (
    <div className={classes.container}>
      <div className={classes.row}>
        <SkeletonField />
      </div>
      <div className={classes.row}>
        <SkeletonField />
        <SkeletonField />
      </div>
      <Divider />
      <div className={classes.cardsRow}>
        <SkeletonCard />
        <SkeletonCard />
      </div>
    </div>
  );
};

const ReprintOrderType: React.FC<{ order: Order }> = ({ order }) => {
  const { classes } = useStyles();
  const clipboard = useClipboard({ timeout: 1500 });
  const parentOrderId = order.reprintParentCustomerOrderId!;

  return (
    <div className={classes.reprintRow}>
      <Body>
        Reprint of{" "}
        <Link
          to={getRoutePath("order:details", {
            customerPublicId: order.reprintParentCustomerPublicId!,
            customerOrderId: parentOrderId,
          })}
          className={classes.link}
        >
          Order# {parentOrderId}
        </Link>
      </Body>
      <ActionIcon size="xs" variant="transparent" onClick={() => clipboard.copy(parentOrderId)}>
        <IconV3 icon="content_copy" size={16} />
      </ActionIcon>
    </div>
  );
};

export const OrderDetailsTab: React.FC = () => {
  const { customerPublicId, customerOrderId } = useParams();
  const user = useAuthUser();
  const { classes } = useStyles();

  const { data, isLoading, error, refetch } = GraphQL.useOrderDetailsQuery(
    {
      orderIdentifier: {
        customerPublicId: customerPublicId ?? "",
        customerOrderId: customerOrderId ?? "",
      },
      isSandbox: user.isSandbox,
    },
    { enabled: !!customerPublicId && !!customerOrderId }
  );

  const order = data?.orderByIdentifier;

  if (isLoading) {
    return <OrderDetailsSkeleton />;
  }

  if (error || !order) {
    return (
      <div className={classes.errorContainer}>
        <Body>Failed to load order details.</Body>
        <Button variant="outlined" intent="neutral" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.row}>
        <DetailField label="Order Type">
          {isReprintOrder(order) ? <ReprintOrderType order={order} /> : "Original"}
        </DetailField>
      </div>

      <div className={classes.row}>
        <DetailField label="Order Date">{order.createdAt ? formatOrderDate(order.createdAt) : "—"}</DetailField>
        {user.is.SUPER_ADMIN && (
          <DetailField label="Organization">
            <Link
              to={getRoutePath("customer", { publicId: order.customer?.publicId ?? customerPublicId! })}
              className={classes.link}
            >
              {order.customer?.organizationName ?? customerPublicId}
            </Link>
          </DetailField>
        )}
      </div>

      <Divider />

      <div className={classes.cardsRow}>
        <PaymentCard order={order} isSandbox={user.isSandbox} />
        {!user.isSandbox && <RefundCard order={order} />}
      </div>
    </div>
  );
};
